/**
 * Product report state management — URL-driven.
 *
 * Every configuration option is stored in the URL query params.
 * This enables deep-linking: opening a URL renders the exact view.
 *
 * Example URL:
 *   /product/new?table=product_events_intraday&events=purchase,app_opened&preset=last_28_days&count=distinct&metric=price_in_try&calc=sum&chart=pivot&row=campaign_name
 */
import { useCallback, useMemo } from 'react';
import { resolveDateRange } from '@/lib/dates';
import { useBulkUrlState } from '@/hooks/useUrlState';

export interface ProductState {
  // Data source
  table: string;
  events: string[];
  // Date range
  start: string;
  end: string;
  preset: string;
  // Comparison date range (pivot only)
  cmpStart: string;
  cmpEnd: string;
  cmpPreset: string;
  // View
  chart: 'ts' | 'pivot';
  interval: string;
  // Count method — all events vs unique users
  count: string;
  // Metric — numeric event parameter field (e.g., 'price_in_try')
  metric: string;
  // Calculation method — sum or avg (requires metric)
  calc: string;
  // Time series options
  cumulative: boolean;
  breakdown: string;
  // Pivot options
  row: string;
  col: string;
  // Custom formula field IDs (comma-separated in URL, e.g., 'fld_abc,fld_xyz')
  formulas: string[];
  // Filters (base64 JSON)
  filters: string;
}

const DEFAULTS: ProductState = {
  table: '',
  events: [],
  start: '',
  end: '',
  preset: 'last_28_days',
  cmpStart: '',
  cmpEnd: '',
  cmpPreset: '',
  chart: 'ts',
  interval: 'd',
  cumulative: false,
  count: 'all',
  metric: '',
  calc: '',
  breakdown: '',
  row: '',
  col: '',
  formulas: [],
  filters: '',
};

/**
 * Read and write product report config from/to URL query params.
 */
export function useProductState() {
  const { state: rawState, update, reset, searchParams } = useBulkUrlState(DEFAULTS, {
    arrayKeys: ['events', 'formulas'],
    booleanKeys: ['cumulative'],
    replace: true,
  });

  // Fix preset: if custom start/end dates are in the URL, don't fall back to a default preset
  const state = useMemo<ProductState>(() => {
    const preset = searchParams.has('start') && !searchParams.has('preset')
      ? ''
      : (rawState.preset as string);
    return {
      ...rawState,
      preset,
      chart: (rawState.chart || 'ts') as ProductState['chart'],
    } as ProductState;
  }, [rawState, searchParams]);

  /** Resolved start/end dates from preset or explicit dates */
  const dates = useMemo(() => {
    return resolveDateRange(state.preset, state.start, state.end);
  }, [state.preset, state.start, state.end]);

  /** Resolved comparison dates (null when not set) */
  const comparisonDates = useMemo(() => {
    if (!state.cmpPreset && !state.cmpStart) return null;
    return resolveDateRange(state.cmpPreset, state.cmpStart, state.cmpEnd);
  }, [state.cmpPreset, state.cmpStart, state.cmpEnd]);

  /** Check if the config has enough info to run a query */
  const canRun = useMemo(() => {
    // Need a table AND at least one event or one custom field
    return Boolean(state.table && (state.events.length > 0 || state.formulas.length > 0));
  }, [state.table, state.events.length, state.formulas.length]);

  /** Build the API request body from current state */
  const toApiPayload = useCallback(() => {
    return {
      tableName: state.table,
      eventNames: state.events,
      startDate: dates.startDate,
      endDate: dates.endDate,
      timeInterval: expandInterval(state.interval),
      countMethod: state.count as 'all' | 'distinct',
      breakdownProperty: state.breakdown || undefined,
      rowDimension: state.row || undefined,
      columnBreakdown: state.col || undefined,
      metricField: state.metric || undefined,
      calculationMethod: state.calc || undefined,
      filters: state.filters ? decodeFiltersFromUrl(state.filters) : [],
    };
  }, [state, dates]);

  return { state, update, reset, canRun, toApiPayload, dates, comparisonDates, searchParams };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function expandInterval(short: string): string {
  const map: Record<string, string> = { h: 'hours', d: 'days', w: 'weeks', m: 'months' };
  return map[short] || short;
}

/**
 * Decode base64-encoded filters from the URL and transform them
 * into the backend-expected FilterItem[] format.
 *
 * Supports both:
 *   - New grouped format: { groups: [ { conditions: [...] }, ... ] }
 *     → Produces FilterGroup items connected by OR
 *   - Legacy flat format: [ { field, operator, value }, ... ]
 *     → Produces simple FilterCondition items connected by AND
 */
function decodeFiltersFromUrl(encoded: string): unknown[] {
  try {
    const parsed = JSON.parse(atob(encoded));

    // New grouped format: { groups: [...] }
    if (parsed && Array.isArray(parsed.groups)) {
      const groups = parsed.groups as Array<{
        id?: string;
        conditions: Array<{ id?: string; field: string; operator: string; value: string }>;
        conditionOperator?: 'AND' | 'OR';
      }>;

      const validGroups = groups.filter((g) => g.conditions.some((c) => c.field && c.value));

      if (validGroups.length === 0) return [];

      // Single group → flat conditions connected by group's conditionOperator
      if (validGroups.length === 1) {
        const condOp: string = validGroups[0].conditionOperator ?? 'AND';
        return validGroups[0].conditions
          .filter((c) => c.field && c.value)
          .map((c, i) => ({
            type: 'filter' as const,
            field: c.field,
            value: c.value,
            comparison: c.operator || '=',
            operator: i === 0 ? null : condOp,
          }));
      }

      // Multiple groups → wrap each in a FilterGroup connected by groupOperator
      const topOp: string = parsed.groupOperator ?? 'OR';
      return validGroups.map((group, gIdx) => ({
        type: 'group' as const,
        groupOperator: group.conditionOperator ?? 'AND',
        operator: gIdx === 0 ? null : topOp,
        children: group.conditions
          .filter((c) => c.field && c.value)
          .map((c, cIdx) => ({
            type: 'filter' as const,
            field: c.field,
            value: c.value,
            comparison: c.operator || '=',
            operator: cIdx === 0 ? null : (group.conditionOperator ?? 'AND'),
          })),
      }));
    }

    // Legacy flat array format
    if (Array.isArray(parsed)) {
      return (parsed as Array<{ id?: string; field: string; operator: string; value: string }>)
        .filter((f) => f.field && f.value)
        .map((f) => ({
          type: 'filter' as const,
          field: f.field,
          value: f.value,
          comparison: f.operator || '=',
          operator: null,
        }));
    }

    return [];
  } catch {
    return [];
  }
}
